<template>
    <div class="rich-text-editor">
        <div class="toolbar" v-if="editor && !readOnly">
            <button
                type="button"
                @click="editor.chain().focus().toggleBold().run()"
                :class="{ 'is-active': editor.isActive('bold') }"
            >
                <q-icon name="format_bold" />
            </button>
            <button
                type="button"
                @click="editor.chain().focus().toggleItalic().run()"
                :class="{ 'is-active': editor.isActive('italic') }"
            >
                <q-icon name="format_italic" />
            </button>
            <button
                type="button"
                @click="editor.chain().focus().toggleUnderline().run()"
                :class="{ 'is-active': editor.isActive('underline') }"
            >
                <q-icon name="format_underlined" />
            </button>
            <button
                type="button"
                @click="
                    editor.chain().focus().toggleHeading({ level: 1 }).run()
                "
                :class="{
                    'is-active': editor.isActive('heading', { level: 1 }),
                }"
            >
                H1
            </button>
            <button
                type="button"
                @click="
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                "
                :class="{
                    'is-active': editor.isActive('heading', { level: 2 }),
                }"
            >
                H2
            </button>
            <button
                type="button"
                @click="editor.chain().focus().toggleBulletList().run()"
                :class="{ 'is-active': editor.isActive('bulletList') }"
            >
                <q-icon name="format_list_bulleted" />
            </button>
            <button
                type="button"
                @click="editor.chain().focus().toggleOrderedList().run()"
                :class="{ 'is-active': editor.isActive('orderedList') }"
            >
                <q-icon name="format_list_numbered" />
            </button>
            <button
                type="button"
                @click="setLink"
                :class="{ 'is-active': editor.isActive('link') }"
            >
                <q-icon name="link" />
            </button>
            <button
                type="button"
                @click="unsetLink"
                v-if="editor.isActive('link')"
            >
                <q-icon name="link_off" />
            </button>
            <button type="button" @click="addImage">
                <q-icon name="image" />
            </button>
            <button
                type="button"
                @click="
                    editor.chain().focus().unsetAllMarks().clearNodes().run()
                "
            >
                <q-icon name="format_clear" />
            </button>
        </div>
        <editor-content :editor="editor" class="editor-content" />
        <input v-if="name" type="hidden" :name="name" :value="content" />
    </div>
</template>

<script setup>
import { useEditor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Markdown } from "tiptap-markdown";
import { Slice, Fragment } from "@tiptap/pm/model";
import { ref, onBeforeUnmount, watch } from "vue";
import { useQuasar } from "quasar";

const CustomImage = Image.extend({
    addStorage() {
        return {
            markdown: {
                serialize(state, node) {
                    const escapeHtml = (unsafe) => {
                        if (!unsafe) return "";
                        return String(unsafe)
                            .replace(/&/g, "&amp;")
                            .replace(/</g, "&lt;")
                            .replace(/>/g, "&gt;")
                            .replace(/"/g, "&quot;")
                            .replace(/'/g, "&#039;");
                    };

                    const widthAttr = node.attrs.width
                        ? ` width="${escapeHtml(node.attrs.width)}"`
                        : "";
                    const titleHtmlAttr = node.attrs.title
                        ? ` title="${escapeHtml(node.attrs.title)}"`
                        : "";

                    if (widthAttr || titleHtmlAttr) {
                        const srcHtml = escapeHtml(node.attrs.src);
                        const altHtml = escapeHtml(node.attrs.alt || "");
                        state.write(
                            `<img src="${srcHtml}" alt="${altHtml}"${titleHtmlAttr}${widthAttr}>`,
                        );
                    } else {
                        const src = state.esc(node.attrs.src);
                        const alt = state.esc(node.attrs.alt || "");
                        state.write(`![${alt}](${src})`);
                    }
                },
                parse: {
                    setup(markdownit) {},
                },
            },
        };
    },
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: null,
                parseHTML: (element) => element.getAttribute("width"),
                renderHTML: (attributes) => {
                    if (!attributes.width) {
                        return {};
                    }
                    return {
                        width: attributes.width,
                    };
                },
            },
        };
    },
});

const props = defineProps({
    name: {
        type: String,
        default: "",
    },
    initialContent: {
        type: String,
        default: "",
    },
    modelValue: {
        type: String,
        default: null,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["update:modelValue"]);

const getInitialValue = () => {
    if (props.modelValue !== null) {
        return props.modelValue ?? "";
    }
    return props.initialContent ?? "";
};

const content = ref(getInitialValue());
const $q = useQuasar();

const editor = useEditor({
    content: getInitialValue(),
    editable: !props.readOnly,
    extensions: [
        StarterKit,
        Underline,
        Link.configure({
            openOnClick: false,
        }),
        CustomImage,
        Markdown.configure({
            html: true,
        }),
    ],
    editorProps: {
        handlePaste(view, event) {
            const text = event.clipboardData?.getData("text/plain");
            if (text) {
                const lines = text.split(/\r\n|\r|\n/);
                const nodes = lines.map((line) => {
                    return view.state.schema.nodes.paragraph.create(
                        null,
                        line ? view.state.schema.text(line) : null,
                    );
                });
                const slice = new Slice(Fragment.from(nodes), 1, 1);
                view.dispatch(
                    view.state.tr.replaceSelection(slice).scrollIntoView(),
                );
                return true;
            }
            return false;
        },
    },
    onUpdate: ({ editor }) => {
        const md = editor.storage.markdown.getMarkdown();
        content.value = md;
        emit("update:modelValue", md);
    },
});

watch(
    () => props.modelValue,
    (newVal) => {
        if (!editor.value) return;
        const val = newVal ?? "";
        const currentMd = editor.value.storage.markdown.getMarkdown();
        if (currentMd !== val) {
            editor.value.commands.setContent(val, false);
            content.value = val;
        }
    },
);

watch(
    () => props.initialContent,
    (newVal) => {
        if (!editor.value || props.modelValue !== null) return;
        const val = newVal ?? "";
        const currentMd = editor.value.storage.markdown.getMarkdown();
        if (currentMd !== val) {
            editor.value.commands.setContent(val, false);
            content.value = val;
        }
    },
);

watch(
    () => props.readOnly,
    (newVal) => {
        if (!editor.value) return;
        editor.value.setEditable(!newVal);
    },
);

const setLink = () => {
    const previousUrl = editor.value.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) {
        return;
    }

    if (url === "") {
        editor.value.chain().focus().extendMarkRange("link").unsetLink().run();

        return;
    }

    editor.value
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
};

const unsetLink = () => {
    editor.value.chain().focus().unsetLink().run();
};

const addImage = () => {
    $q.dialog({
        title: "Image URL",
        message: "Enter the URL of the image:",
        prompt: {
            model: "",
            type: "url",
        },
        cancel: true,
        persistent: true,
    }).onOk((url) => {
        if (url) {
            $q.dialog({
                title: "Image Width",
                message:
                    "Enter the width of the image (optional, e.g., 100%, 500px):",
                prompt: {
                    model: "",
                    type: "text",
                },
                cancel: true,
                persistent: true,
            })
                .onOk((width) => {
                    if (width) {
                        editor.value.commands.insertContent(
                            `<img src="${url}" width="${width}">`,
                        );
                    } else {
                        editor.value
                            .chain()
                            .focus()
                            .setImage({ src: url })
                            .run();
                    }
                })
                .onCancel(() => {
                    // Fallback if width input is cancelled but url is given
                    editor.value.chain().focus().setImage({ src: url }).run();
                });
        }
    });
};

onBeforeUnmount(() => {
    editor.value?.destroy();
});
</script>

<style lang="scss">
.rich-text-editor {
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: white;

    .toolbar {
        padding: 0.5rem;
        border-bottom: 1px solid #ccc;
        background-color: #f8f9fa;
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;

        button {
            padding: 0.25rem 0.5rem;
            border: 1px solid #ddd;
            background: white;
            border-radius: 3px;
            cursor: pointer;
            min-width: 30px;
            color: black;

            &.is-active {
                background-color: #e2e6ea;
                border-color: #adb5bd;
            }

            &:hover {
                background-color: #f1f3f5;
            }
        }
    }

    .editor-content {
        min-height: 200px;
        padding: 0.5rem;
        cursor: text;

        .ProseMirror {
            outline: none;
            min-height: 200px;

            p {
                margin-bottom: 0.5rem;
            }

            ul,
            ol {
                padding-left: 1.5rem;
                margin-bottom: 0.5rem;
            }

            a {
                color: #007bff;
                text-decoration: underline;
            }
        }
    }
}
</style>
